import React, { FC, useCallback, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { FormLayout, FormLayoutGroup, ScreenSpinner, Separator } from '@vkontakte/vkui';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';

import { RootState } from 'src/core/rootReducer';
import { BaseGame, ChestSettings } from 'src/games/common/types';
import { ColorSelect, GameStyleSelect, ImageSelect } from 'src/vk-app/components';
import { myGamesActions } from 'src/store/my-games/actions';
import { vkActions, VKInitialState } from 'src/store/vk/reducer';
import { AllGamesInitState } from 'src/store/game/reducers/allGamesReducer';
import { GameId } from 'src/store/game/types';
import { usePrevious } from 'src/core/hooks/usePrevious';
import { config } from 'src/config/config';
import { nyBackgrounds } from '../common/design';

type Props = {
  initialValues: ChestSettings;
};

const Design: FC<Props> = () => {
  const formSelector = formValueSelector('game');
  const values = useSelector<RootState>((state) => state.form.game.values) as ChestSettings;
  const dispatch = useDispatch();

  const vkData = useSelector<RootState, VKInitialState>((state) => state.vk);

  const handleSubmit = useCallback(() => {
    dispatch(vkActions.setPopoutContent({ popout: <ScreenSpinner /> }));
    dispatch(myGamesActions.saveSettings.started({ gameId: values.gameId }));
  }, [dispatch, values.gameId]);

  const chestVariants = useSelector<RootState, string[]>((state) =>
    formSelector(state, 'chestVariants')
  );

  const mainGameSettingsId = useSelector<RootState, number>((state) =>
    formSelector(state, 'mainGameSettingsId')
  );

  const games = useSelector<RootState, AllGamesInitState>((state) => state.games.allGames);

  const gameInfo = games.data.find((game) => game.gameId === GameId.CHEST);

  const prevRequestSave = usePrevious(vkData.requestSave);

  useEffect(() => {
    if (vkData.requestSave && !prevRequestSave) {
      handleSubmit();
    }
  }, [vkData.requestSave, handleSubmit, prevRequestSave]);

  const view = useSelector<RootState, BaseGame['view']>((state) => formSelector(state, 'view'));
  const secondSectionBgColor = useSelector<RootState, string>((state) =>
    formSelector(state, 'secondSectionBgColor')
  );
  const backgroundColor = useSelector<RootState, string>((state) =>
    formSelector(state, 'backgroundColor')
  );

  const textPreviewBgColor = view === 'classic' ? secondSectionBgColor : backgroundColor;

  return (
    <Wrap>
      <FormLayout>
        <FormLayoutGroup top="Цвет заголовка">
          <Field
            name="titleColor"
            component={ColorSelect}
            TextPreview={
              <TitlePreview color={values.titleColor} backgroundColor={textPreviewBgColor}>
                {values.titleText}
              </TitlePreview>
            }
          />
        </FormLayoutGroup>

        <Separator />

        <FormLayoutGroup top="Цвет подзаголовка">
          <Field
            name="subtitleColor"
            component={ColorSelect}
            TextPreview={
              <SubtitlePreview color={values.subtitleColor} backgroundColor={textPreviewBgColor}>
                {values.subtitleText}
              </SubtitlePreview>
            }
          />
        </FormLayoutGroup>

        <Separator />

        <ChestsWrap>
          <FormLayoutGroup className="chest">
            <LabelsWrap>
              <Label className="FormLayout__row-top">Сундук #1</Label>
              <LabelDescription className="FormLayout__row-top">
                Отображается только на десктопе
              </LabelDescription>
            </LabelsWrap>

            <Field
              name="chests[0]"
              component={ImageSelect}
              variants={chestVariants}
              title="Выберите коробку"
            />
          </FormLayoutGroup>

          <FormLayoutGroup className="chest">
            <LabelsWrap>
              <Label className="FormLayout__row-top">Сундук #2</Label>
              <LabelDescription className="FormLayout__row-top">
                Отображается только на десктопе
              </LabelDescription>
            </LabelsWrap>

            <Field
              name="chests[1]"
              component={ImageSelect}
              variants={chestVariants}
              title="Выберите коробку"
            />
          </FormLayoutGroup>

          <FormLayoutGroup className="chest">
            <Label className="FormLayout__row-top">Сундук #3</Label>
            <Field
              name="chests[2]"
              component={ImageSelect}
              variants={chestVariants}
              title="Выберите коробку"
            />
          </FormLayoutGroup>

          <FormLayoutGroup className="chest">
            <Label className="FormLayout__row-top">Сундук #4</Label>
            <Field
              name="chests[3]"
              component={ImageSelect}
              variants={chestVariants}
              title="Выберите коробку"
            />
          </FormLayoutGroup>

          <FormLayoutGroup className="chest">
            <Label className="FormLayout__row-top">Сундук #5</Label>
            <Field
              name="chests[4]"
              component={ImageSelect}
              variants={chestVariants}
              title="Выберите коробку"
            />
          </FormLayoutGroup>
        </ChestsWrap>

        <Separator />

        <FormLayoutGroup top="Внешний вид">
          <GameStyleSelect />
        </FormLayoutGroup>

        <FormLayoutGroup top="Цвет фона">
          <Field
            name={view === 'classic' ? 'secondSectionBgColor' : 'backgroundColor'}
            component={ColorSelect}
            top="Цвет фона"
          />
        </FormLayoutGroup>

        <FormLayoutGroup top="Фон">
          <Field
            name="backgroundImgSrc"
            component={ImageSelect}
            variants={[...(gameInfo?.backgroundVariants || []), ...nyBackgrounds]}
            title="Выберите фон"
            className="select-background"
            size={[200, 200]}
            uploadUrl={`${config.api}/vk/upload-game-background/${mainGameSettingsId}`}
            setPreviewSrc={(src: string) => {
              dispatch(change('game', 'backgroundImgSrc', src));
            }}
            backgroundSize="cover"
            canUpload={true}
            disabled={view === 'mono'}
          />
        </FormLayoutGroup>
      </FormLayout>
    </Wrap>
  );
};

const Wrap = styled.div`
  .select-background {
    margin: 0 12px;
  }
`;

const TitlePreview = styled.div<{ color?: string; backgroundColor?: string }>`
  margin: 0 12px;
  font-size: 24px;
  font-weight: bold;
  color: ${(p) => p.color};
  background-color: ${(p) => p.backgroundColor};
`;

const SubtitlePreview = styled.div<{ color?: string; backgroundColor?: string }>`
  margin: 0 12px;
  font-size: 16px;
  line-height: 20px;
  color: ${(p) => p.color};
  background-color: ${(p) => p.backgroundColor};
`;

const ChestsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;

  .chest {
    margin: 12px;
  }
`;

const Label = styled.span``;

const LabelsWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 150px;
`;

const LabelDescription = styled.div`
  font-size: 10px;
  line-height: 12px;
`;

const withForm = reduxForm<any, any, any>({
  form: 'game',
})(Design);

const withStore = connect((state: RootState) => {
  const { mainSettingsId } = state.vk.gameSettings;

  return {
    initialValues: state.myGames.data.find(
      (game: any) => game.mainGameSettings.id === mainSettingsId
    ),
  };
}, {})(withForm);

export { withStore as Design };
